import { useRef } from "react";
import {
  Calendar, Download, AlertTriangle, Quote, ArrowRight, Mail, Users, FileText, Building2,
  Maximize2, ExternalLink,
} from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { AdoptionMap } from "@/components/bu/AdoptionMap";
import { CountryRoiCalculator } from "@/components/bu/CountryRoiCalculator";
import { OutletGrowthChart } from "@/components/OutletGrowthChart";
import {
  commitments, rolloutPhases, caseStudies, risks, orgImpact, peerContacts,
} from "@/data/buOwnerData";

const QUICKSIGHT_URL =
  "https://eu-central-1.quicksight.aws.amazon.com/sn/account/franjovucic/embed/share/accounts/575108936344/dashboards/db64862e-a36b-4988-8fd2-fff997855497";

const SCHEDULE_MAILTO =
  "mailto:martina.lovetinska@cchellenic.com,pero.pehar@master-fb.com,igor.guberovic@cchellenic.com,katarzyna.stanczak@cchellenic.com" +
  `?subject=${encodeURIComponent("FORA — Country adoption discovery")}` +
  `&body=${encodeURIComponent(
    "Hi team,\n\nI'd like to schedule a country-adoption discovery call for my CC HBC business unit.\n\nPlease suggest a few available time slots.\n\nBest regards"
  )}`;

const probabilityImpactColor = (v) => {
  if (v === "High") return "bg-[#dd260e] text-white";
  if (v === "Medium") return "bg-[#f59e0b] text-white";
  return "bg-[#e8e8e8] text-[#555555]";
};

const ownerBadgeColor = (owner) => {
  if (owner === "BU") return "bg-[#000000] text-white";
  if (owner === "MFB") return "bg-[#dd260e] text-white";
  return "bg-[#e8e8e8] text-[#555555]";
};

export const BUOwnerDeck = () => {
  const dashboardRef = useRef(null);

  const enterFullscreen = () => {
    const el = dashboardRef.current;
    if (!el) return;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  return (
    <div data-testid="bu-owner-deck-view">
      {/* ── HERO ────────────────────────────────────────────── */}
      <div className="mb-14" data-testid="bu-hero">
        <div className="fora-pattern rounded-sm p-8 sm:p-12 lg:p-16 relative overflow-hidden">
          <div className="relative z-10">
            <span className="fora-eyebrow-dot mb-6">For CC HBC Business Unit Owners</span>
            <h1 className="font-condensed text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.95] mb-6 mt-4 max-w-4xl">
              Bring FORA to your country.
            </h1>
            <p className="text-white/60 text-sm sm:text-base max-w-2xl mb-10 leading-relaxed">
              Everything a BU Owner needs to make a go/no-go decision: proof from peer markets, country-level ROI, what we ask of you, risks honestly mapped, and a path from kickoff to scale.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={SCHEDULE_MAILTO} className="cchbc-btn cchbc-btn-primary" data-testid="bu-hero-schedule" style={{ background: "#dd260e" }}>
                <Calendar className="w-3.5 h-3.5" /> Book a discovery call
              </a>
              <button
                type="button"
                onClick={() => {
                  const original = document.title;
                  document.title = "FORA-Country-Adoption-Proposal";
                  window.print();
                  setTimeout(() => { document.title = original; }, 200);
                }}
                className="cchbc-btn cchbc-btn-outline-light"
                data-testid="bu-hero-download"
              >
                <Download className="w-3.5 h-3.5" /> Download as PDF
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── ADOPTION MAP ────────────────────────────────────── */}
      <AdoptionMap />

      {/* ── OUTLET GROWTH CHART (existing component) ────────── */}
      <div className="mb-14" data-testid="growth-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">12-Month Network Trajectory</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            FORA outlets — past 12 months
          </h2>
        </div>
        <div className="bg-white border border-[#e8e8e8] rounded-sm p-6">
          <OutletGrowthChart />
        </div>
      </div>

      {/* ── LIVE QUICKSIGHT DASHBOARD ───────────────────────── */}
      <div className="mb-14" data-testid="quicksight-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <span className="cchbc-eyebrow">Live Data &middot; AWS QuickSight</span>
            <span className="inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] border border-[#dd260e]/40 px-2 py-1 rounded-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-[#dd260e] animate-pulse" />
              Demo data
            </span>
          </div>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            Real-time FORA dashboard
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            Live numbers across all FORA markets &mdash; powered by AWS QuickSight. The same dashboard your country team will have on day one of go-live.
          </p>
        </div>

        <div className="bg-[#fafafa] border-l-[3px] border-[#dd260e] p-4 mb-4 rounded-sm" data-testid="quicksight-demo-notice">
          <p className="text-xs text-[#000000] font-semibold uppercase tracking-[0.1em] mb-1">Showcase only</p>
          <p className="text-xs text-[#555555] leading-relaxed">
            The numbers shown below are <strong className="text-[#000000]">not real</strong> &mdash; this preview demonstrates the reporting capabilities and visual layout your BU will get with live FORA data once the integration goes live in your market.
          </p>
        </div>

        <div
          ref={dashboardRef}
          className="bg-[#000000] rounded-sm overflow-hidden border border-[#e8e8e8] relative group"
          data-testid="dashboard-wrapper"
        >
          <div className="absolute top-3 right-3 z-10 flex gap-2">
            <a
              href={QUICKSIGHT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm transition-colors"
              data-testid="dashboard-open-tab-btn"
              aria-label="Open dashboard in new tab"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">New tab</span>
            </a>
            <button
              type="button"
              onClick={enterFullscreen}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#dd260e] hover:bg-white hover:text-[#000000] text-white text-[10px] font-bold uppercase tracking-[0.15em] rounded-sm transition-colors"
              data-testid="dashboard-fullscreen-btn"
              aria-label="View dashboard fullscreen"
            >
              <Maximize2 className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Fullscreen</span>
            </button>
          </div>

          <div className="w-full h-[calc(100vh-280px)] min-h-[520px] sm:min-h-[640px] lg:h-[860px]">
            <iframe
              title="FORA Live Metrics — AWS QuickSight"
              src={QUICKSIGHT_URL}
              className="w-full h-full border-0"
              allow="fullscreen"
              allowFullScreen
              loading="lazy"
              data-testid="quicksight-iframe"
            />
          </div>
        </div>
        <p className="text-[10px] text-[#555555] italic uppercase tracking-[0.1em] mt-3">
          Dashboard embedded from AWS QuickSight &mdash; access requires a Coca-Cola HBC account session.
        </p>
      </div>

      {/* ── COUNTRY ROI CALCULATOR ──────────────────────────── */}
      <CountryRoiCalculator />

      {/* ── COMMITMENT MATRIX ───────────────────────────────── */}
      <div className="mb-14" data-testid="commitment-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">The Ask</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            What we need from you
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            Radical transparency. Here&apos;s exactly what your BU brings to the rollout, and where Master F&amp;B picks up.
          </p>
        </div>
        <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#fafafa] border-b border-[#e8e8e8] text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555]">
            <div className="col-span-3">Resource</div>
            <div className="col-span-3">Commitment</div>
            <div className="col-span-4">Why it matters</div>
            <div className="col-span-1">When</div>
            <div className="col-span-1">Owner</div>
          </div>
          {commitments.map((c, i) => (
            <div
              key={c.resource}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 ${i < commitments.length - 1 ? "border-b border-[#e8e8e8]" : ""}`}
              data-testid={`commitment-row-${i}`}
            >
              <div className="md:col-span-3">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">Resource</p>
                <p className="font-condensed text-base font-black uppercase tracking-tight text-[#000000]">{c.resource}</p>
              </div>
              <div className="md:col-span-3">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">Commitment</p>
                <p className="text-xs text-[#555555]">{c.effort}</p>
              </div>
              <div className="md:col-span-4">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">Why it matters</p>
                <p className="text-xs text-[#555555] leading-relaxed">{c.why}</p>
              </div>
              <div className="md:col-span-1 flex md:block items-center gap-2">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">When:</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#000000]">{c.when}</p>
              </div>
              <div className="md:col-span-1 flex md:block items-center gap-2">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">Owner:</p>
                <span className={`inline-block text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm ${ownerBadgeColor(c.owner)}`}>
                  {c.owner}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ROLLOUT PLAYBOOK (Gantt) ────────────────────────── */}
      <div className="mb-14" data-testid="playbook-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">Rollout Playbook</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            From discovery to saturation, in 24 months
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            Realistic tempo from HR / PL deployments. Every phase: deliverables, your team&apos;s effort, ours, and the KPI we measure.
          </p>
        </div>

        {/* Gantt strip */}
        <div className="bg-white border border-[#e8e8e8] rounded-sm p-6 mb-4 hidden md:block" data-testid="playbook-gantt">
          <div className="flex flex-col gap-2">
            {rolloutPhases.map((p, i) => {
              const left = rolloutPhases.slice(0, i).reduce((s, x) => s + x.width, 0);
              const labelInside = p.width >= 18;
              const bg = i === 0 ? "#000" : i === 1 ? "#dd260e" : i === 2 ? "#3b82f6" : "#16a34a";
              return (
                <div key={p.name} className="relative h-9">
                  <div
                    className="absolute h-full rounded-sm flex items-center px-3"
                    style={{
                      left: `${left}%`,
                      width: `${p.width}%`,
                      backgroundColor: bg,
                    }}
                  >
                    {labelInside && (
                      <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white whitespace-nowrap">
                        {p.name} &middot; M{p.months}
                      </p>
                    )}
                  </div>
                  {!labelInside && (
                    <p
                      className="absolute top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-[0.12em] text-[#000000] whitespace-nowrap pl-2"
                      style={{ left: `${left + p.width}%` }}
                    >
                      {p.name} &middot; M{p.months}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex justify-between text-[9px] text-[#555555] mt-3 pt-3 border-t border-[#e8e8e8]">
            <span>M0</span>
            <span>M6</span>
            <span>M12</span>
            <span>M18</span>
            <span>M24</span>
          </div>
        </div>

        {/* Phase detail cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {rolloutPhases.map((p, i) => (
            <div
              key={p.name}
              className="bg-white border border-[#e8e8e8] rounded-sm p-5"
              data-testid={`phase-card-${i}`}
            >
              <span className="font-condensed text-xs font-black tracking-tight text-white bg-[#dd260e] px-2 py-0.5 rounded-sm inline-block mb-3">
                Phase {String(i + 1).padStart(2, "0")} &middot; M{p.months}
              </span>
              <p className="font-condensed text-lg font-black uppercase tracking-tight text-[#000000] mb-3">{p.name}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555] mb-2">Deliverables</p>
              <ul className="text-[11px] text-[#555555] leading-relaxed space-y-1 mb-4">
                {p.deliverables.map((d) => (
                  <li key={d} className="flex gap-1.5"><span className="text-[#dd260e]">—</span><span>{d}</span></li>
                ))}
              </ul>
              <div className="pt-3 border-t border-[#e8e8e8] space-y-2">
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#000000]">BU effort</p>
                  <p className="text-[11px] text-[#555555]">{p.buEffort}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">Master F&amp;B</p>
                  <p className="text-[11px] text-[#555555]">{p.mfbEffort}</p>
                </div>
                <div>
                  <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-[#555555]">KPI</p>
                  <p className="text-[11px] text-[#000000] font-semibold">{p.kpi}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── COUNTRY CASE STUDIES ────────────────────────────── */}
      <div className="mb-14" data-testid="case-studies-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">Live Country Case Studies</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            Peers who already adopted
          </h2>
        </div>
        <div className="space-y-4">
          {caseStudies.map((cs, idx) => {
            // Derive ISO country code from flag emoji (🇭🇷 → "HR")
            const code = Array.from(cs.flag || "")
              .slice(0, 2)
              .map((c) => String.fromCharCode((c.codePointAt(0) || 0) - 0x1f1e6 + 65))
              .join("");
            const primaryMetric = cs.metrics[0];
            const tones = ["#000000", "#dd260e", "#1a1a1a"];
            const panelBg = tones[idx % tones.length];
            return (
              <div
                key={cs.country}
                className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden grid grid-cols-1 md:grid-cols-12"
                data-testid={`case-study-${cs.country.toLowerCase()}`}
              >
                {/* Country panel — CSS-only, no image dependency */}
                <div
                  className="md:col-span-4 relative overflow-hidden flex flex-col justify-between p-7 min-h-[14rem] md:min-h-0"
                  style={{
                    background: `linear-gradient(155deg, ${panelBg} 0%, ${panelBg} 60%, rgba(255,255,255,0.06) 100%)`,
                  }}
                  aria-label={`${cs.country} case study visual`}
                >
                  {/* Background mega-typography */}
                  <span
                    className="absolute font-condensed font-black text-white/[0.06] leading-none select-none pointer-events-none"
                    style={{ fontSize: "clamp(160px, 28vw, 320px)", right: "-2rem", bottom: "-3rem", letterSpacing: "-0.08em" }}
                  >
                    {code}
                  </span>
                  <div className="relative z-10">
                    <div className="flex items-center gap-3">
                      <span className="text-3xl drop-shadow">{cs.flag}</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/80">
                        {cs.country}
                      </span>
                    </div>
                  </div>
                  <div className="relative z-10">
                    <p className="font-condensed text-5xl sm:text-6xl text-white font-black leading-none tabular-nums">
                      {primaryMetric.value}
                    </p>
                    <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 mt-2">
                      {primaryMetric.label}
                    </p>
                  </div>
                </div>
                <div className="md:col-span-8 p-8 flex flex-col">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">{cs.flag}</span>
                    <span className="cchbc-eyebrow">{cs.country} &middot; {cs.timeframe}</span>
                  </div>
                  <h3 className="font-condensed text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#000000] mb-4 leading-tight">
                    {cs.headline}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 my-5 pt-5 border-t border-[#e8e8e8]">
                    {cs.metrics.map((m) => (
                      <div key={m.label}>
                        <p className="font-condensed text-2xl text-[#dd260e] leading-none tabular-nums">{m.value}</p>
                        <p className="text-[9px] font-bold uppercase tracking-[0.15em] text-[#555555] mt-1">{m.label}</p>
                      </div>
                    ))}
                  </div>
                <div className="mt-auto pt-5 border-t border-[#e8e8e8]">
                  <Quote className="w-5 h-5 text-[#dd260e] mb-2" strokeWidth={1.5} />
                  <p className="text-sm italic text-[#000000] leading-relaxed mb-3">&ldquo;{cs.quote}&rdquo;</p>
                  <div className="flex items-center gap-2">
                    <span className="w-6 h-[2px] bg-[#dd260e]" />
                    <div>
                      <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#000000]">{cs.author}</p>
                      <p className="text-[10px] text-[#555555]">{cs.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* ── RISK REGISTER ───────────────────────────────────── */}
      <div className="mb-14" data-testid="risk-register-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">Risk Register</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3 flex items-center gap-3">
            <AlertTriangle className="w-7 h-7 text-[#dd260e]" strokeWidth={1.8} />
            What could go wrong &mdash; and our mitigation
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            Board-ready risk register. Every risk we&apos;ve hit before is documented, with a mitigation plan and an owner.
          </p>
        </div>
        <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-[#fafafa] border-b border-[#e8e8e8] text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555]">
            <div className="col-span-3">Risk</div>
            <div className="col-span-1">Prob.</div>
            <div className="col-span-1">Impact</div>
            <div className="col-span-5">Mitigation</div>
            <div className="col-span-2">Owner</div>
          </div>
          {risks.map((r, i) => (
            <div
              key={r.risk}
              className={`grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-4 ${i < risks.length - 1 ? "border-b border-[#e8e8e8]" : ""}`}
              data-testid={`risk-row-${i}`}
            >
              <div className="md:col-span-3">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">Risk</p>
                <p className="text-sm font-semibold text-[#000000]">{r.risk}</p>
              </div>
              <div className="md:col-span-1 flex md:block items-center gap-2">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">Prob.:</p>
                <span className={`inline-block text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm ${probabilityImpactColor(r.probability)}`}>
                  {r.probability}
                </span>
              </div>
              <div className="md:col-span-1 flex md:block items-center gap-2">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">Impact:</p>
                <span className={`inline-block text-[9px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-sm ${probabilityImpactColor(r.impact)}`}>
                  {r.impact}
                </span>
              </div>
              <div className="md:col-span-5">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">Mitigation</p>
                <p className="text-xs text-[#555555] leading-relaxed">{r.mitigation}</p>
              </div>
              <div className="md:col-span-2 flex md:block items-center gap-2">
                <p className="md:hidden text-[9px] font-bold uppercase tracking-[0.18em] text-[#dd260e]">Owner:</p>
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#000000]">{r.owner}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── ORG IMPACT ──────────────────────────────────────── */}
      <div className="mb-14" data-testid="org-impact-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">Organisational Impact</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3">
            What changes for your organisation
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            FORA products are <strong className="text-[#000000]">plug-and-play</strong> — minimum effort from BDs. With FORA in their hands, BDs build stronger relationships and stay in closer contact with venue managers. Here&apos;s what we&apos;ve seen in HR / PL / SI.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          {orgImpact.map((o, i) => (
            <div key={o.team} className="bg-white border border-[#e8e8e8] rounded-sm p-5" data-testid={`org-card-${i}`}>
              <p className="font-condensed text-lg font-black uppercase tracking-tight text-[#000000] mb-3">{o.team}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555] mb-1">From</p>
              <p className="text-xs text-[#555555] leading-relaxed mb-3">{o.from}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#dd260e] mb-1">To</p>
              <p className="text-xs text-[#000000] leading-relaxed mb-3">{o.to}</p>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#555555] mb-1">Benefit</p>
              <p className="text-xs text-[#555555] leading-relaxed italic">{o.benefit}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── PEER CONTACTS ───────────────────────────────────── */}
      <div className="mb-14" data-testid="peer-contacts-section">
        <div className="mb-6 pb-4 border-b border-[#e8e8e8]">
          <span className="cchbc-eyebrow mb-3">Peer-to-Peer</span>
          <h2 className="font-condensed text-3xl sm:text-4xl font-black uppercase tracking-tight text-[#000000] mt-3 flex items-center gap-3">
            <Users className="w-7 h-7 text-[#dd260e]" strokeWidth={1.8} />
            Talk to a BU Owner who&apos;s already done this
          </h2>
          <p className="text-sm text-[#555555] mt-2 max-w-2xl leading-relaxed">
            Skip the vendor pitch. Pick a peer&apos;s brain &mdash; what worked, what didn&apos;t, what they&apos;d do differently.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {peerContacts.map((p) => (
            <a
              key={p.country}
              href={p.email}
              className="bg-white border border-[#e8e8e8] rounded-sm p-5 hover:border-[#000000] transition-colors group block"
              data-testid={`peer-contact-${p.country.toLowerCase()}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{p.flag}</span>
                <span className="cchbc-eyebrow">{p.country}</span>
              </div>
              <p className="font-condensed text-lg font-black uppercase tracking-tight text-[#000000] mb-1 group-hover:text-[#dd260e] transition-colors">{p.name}</p>
              <p className="text-[11px] text-[#555555] mb-3">{p.role}</p>
              <p className="text-xs text-[#555555] leading-relaxed mb-4">{p.topic}</p>
              <span className="cchbc-link text-[10px]"><Mail className="w-3 h-3" /> Request intro</span>
            </a>
          ))}
        </div>
      </div>

      {/* ── FINAL CTA ───────────────────────────────────────── */}
      <div className="bg-[#000000] rounded-sm p-8 sm:p-12 mb-14" data-testid="final-cta-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <span className="cchbc-eyebrow light mb-4">Decision Time</span>
            <p className="font-condensed text-white text-3xl sm:text-4xl uppercase tracking-tight leading-tight mt-4 mb-4">
              Ready to build a country proposal?
            </p>
            <p className="text-white/60 text-sm leading-relaxed max-w-xl">
              Book a 30-min discovery call. We&apos;ll come back to your board with a 4-page proposal customised for your market — outlets, ROI, commitment, timeline.
            </p>
          </div>
          <div className="lg:col-span-5 flex flex-col gap-3 lg:justify-center">
            <a
              href={SCHEDULE_MAILTO}
              className="cchbc-btn cchbc-btn-primary justify-center"
              style={{ background: "#dd260e" }}
              data-testid="final-cta-schedule"
            >
              <Calendar className="w-3.5 h-3.5" /> Book a discovery call
            </a>
            <button
              type="button"
              onClick={() => {
                const original = document.title;
                document.title = "FORA-Country-Adoption-Proposal";
                window.print();
                setTimeout(() => { document.title = original; }, 200);
              }}
              className="cchbc-btn cchbc-btn-outline-light justify-center"
              data-testid="final-cta-pdf"
            >
              <FileText className="w-3.5 h-3.5" /> Export this page as PDF
            </button>
          </div>
        </div>
      </div>

      <PageFooter />
    </div>
  );
};
